// src/components/common/AttendanceCalendar.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Calendar,
    CheckCircle,
    XCircle,
    Award,
    Coffee,
    ChevronLeft,
    ChevronRight,
    Filter,
    AlertTriangle,
    Clock,
    Zap,
    Home,
    LogIn,
    LogOut,
    X
} from 'lucide-react';
import {
    toISTDateString,
    getISTDateFromUTC,
    isSameISTDay,
    getISTComponents,
    formatTime,
    formatDate
} from '../../utils/dateHelpers';
import attendanceService from '../../services/attendance.service';
import holidayPlanService from '../../services/holidayPlan.service';
import leaveService from '../../services/leave.service';

// Helper function to format date in IST timezone (YYYY-MM-DD)
const formatLocalDate = (date = new Date()) => {
    return toISTDateString(date);
};

// Normalize timestamps coming from the API
const parseApiTimestamp = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp instanceof Date) {
        return Number.isNaN(timestamp.getTime()) ? null : timestamp;
    }

    const trimmed = String(timestamp).trim();
    if (!trimmed) return null;

    if (/([Zz]|[+-]\d{2}:?\d{2})$/.test(trimmed)) {
        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const normalized = trimmed.replace('T', ' ');
    const localMatch = normalized.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?)?$/
    );

    if (localMatch) {
        const [, year, month, day, hour = '0', minute = '0', second = '0', fractional = '0'] = localMatch;
        const milliseconds = fractional
            ? Math.round(parseFloat(`0.${fractional}`) * 1000)
            : 0;

        const pad = (n) => String(n).padStart(2, '0');
        const isoWithIST = `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}.${String(milliseconds).padStart(3, '0')}+05:30`;
        const parsed = new Date(isoWithIST);

        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const AttendanceCalendar = ({
    employeeId = null,
    employeeDetails = null,
    isOwnProfile = true,
    compact = false,
    showFilters = true,
    onDayClick = null,
    onDataLoaded = null,  // Callback when attendance data is fetched
    isAdmin = false,  // Whether current user is admin (for revoke functionality)
    onRevokeAttendance = null  // Callback when attendance is revoked
}) => {
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [calendarFilter, setCalendarFilter] = useState('all');
    const [hoveredDate, setHoveredDate] = useState(null);
    const [attendanceData, setAttendanceData] = useState(null);
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(false);

    // Day details modal state
    const [selectedDayDetails, setSelectedDayDetails] = useState(null);
    const [showDayModal, setShowDayModal] = useState(false);

    // Fetch attendance data
    const fetchAttendanceData = useCallback(async () => {
        setLoading(true);
        try {
            let response;
            if (isOwnProfile) {
                // Fetch own attendance
                response = await attendanceService.getMyAttendance(selectedMonth + 1, selectedYear);
            } else if (employeeId) {
                // Fetch specific employee's attendance (admin viewing employee)
                response = await attendanceService.getEmployeeAttendance(employeeId, selectedMonth + 1, selectedYear);
            }

            // Fetch leaves separately and merge them (like EmployeeAttendance.js does)
            let allLeaves = [];
            try {
                if (isOwnProfile) {
                    allLeaves = await leaveService.getLeaveHistory();
                } else if (employeeId) {
                    allLeaves = await leaveService.getEmployeeLeaveHistory(employeeId);
                }
            } catch (e) {
                console.warn('Could not fetch leaves for calendar:', e);
            }

            // Generate leave records for each day in leave period
            const leaveRecordsForMonth = [];
            for (const leave of allLeaves) {
                if (!['approved', 'pending'].includes(leave.status)) continue;

                const fromDateStr = getISTDateFromUTC(leave.from_date);
                const toDateStr = getISTDateFromUTC(leave.to_date);
                const [fromYear, fromMonth, fromDay] = fromDateStr.split('-').map(Number);
                const [toYear, toMonth, toDay] = toDateStr.split('-').map(Number);

                let currentDate = new Date(fromYear, fromMonth - 1, fromDay);
                const endDate = new Date(toYear, toMonth - 1, toDay);

                while (currentDate <= endDate) {
                    if (currentDate.getMonth() === selectedMonth && currentDate.getFullYear() === selectedYear) {
                        const dateStr = toISTDateString(currentDate);
                        // Check if there's already an attendance record for this date
                        const hasAttendance = response?.monthly?.some(r => getISTDateFromUTC(r.date) === dateStr);

                        if (!hasAttendance) {
                            leaveRecordsForMonth.push({
                                date: dateStr,
                                status: 'leave',
                                leave_type: leave.leave_type || leave.leave_type_name || 'Leave',
                                leave_status: leave.status
                            });
                        }
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                }
            }

            // Merge attendance and leave records
            if (response) {
                const mergedMonthly = [...(response.monthly || []), ...leaveRecordsForMonth];
                response.monthly = mergedMonthly;
                setAttendanceData(response);
                // Call the callback with fetched data for stats calculation
                onDataLoaded?.(response);
            }
        } catch (error) {
            console.error('Error fetching attendance:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedMonth, selectedYear, isOwnProfile, employeeId, onDataLoaded]);

    // Fetch holidays (filtered to exclude unapproved optional holidays)
    const fetchHolidays = useCallback(async () => {
        if (!employeeDetails?.holiday_plan_id) return;

        try {
            // Fetch all holidays from the plan
            const response = await holidayPlanService.getHolidayPlanById(employeeDetails.holiday_plan_id);
            if (!response?.holidays) return;

            // Fetch approved optional holidays for this employee
            let approvedOptionalHolidayIds = new Set();
            try {
                if (isOwnProfile) {
                    // For own profile, get my requests and filter approved
                    const myRequests = await holidayPlanService.getMyOptionalHolidayRequests();
                    approvedOptionalHolidayIds = new Set(
                        myRequests
                            .filter(r => r.status === 'approved')
                            .map(r => r.holiday_date_id)
                    );
                } else if (employeeId) {
                    // For admin viewing employee, get approved holidays for that employee
                    const approvedHolidays = await holidayPlanService.getApprovedOptionalHolidays(employeeId);
                    approvedOptionalHolidayIds = new Set(
                        approvedHolidays.map(h => h.holiday_date_id || h.id)
                    );
                }
            } catch (err) {
                console.warn('Could not fetch approved optional holidays:', err);
            }

            // Filter holidays: include non-optional + approved optional holidays
            const filteredHolidays = response.holidays.filter(holiday => {
                // Check for optional flag (support both field names)
                const isOptional = holiday.is_optional || holiday.isOptional;

                if (!isOptional) {
                    // Non-optional holidays are always shown
                    return true;
                }
                // Optional holidays only shown if approved for this employee
                return approvedOptionalHolidayIds.has(holiday.id);
            });

            setHolidays(filteredHolidays);
        } catch (error) {
            console.error('Error fetching holidays:', error);
        }
    }, [employeeDetails?.holiday_plan_id, isOwnProfile, employeeId]);

    useEffect(() => {
        fetchAttendanceData();
    }, [fetchAttendanceData]);

    useEffect(() => {
        fetchHolidays();
    }, [fetchHolidays]);

    // Generate calendar days
    const getCalendarDays = () => {
        const firstDay = new Date(selectedYear, selectedMonth, 1);
        const lastDay = new Date(selectedYear, selectedMonth + 1, 0);
        const startPadding = firstDay.getDay();
        const days = [];

        // Previous month padding
        for (let i = startPadding - 1; i >= 0; i--) {
            const date = new Date(selectedYear, selectedMonth, -i);
            days.push(date);
        }

        // Current month days
        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push(new Date(selectedYear, selectedMonth, i));
        }

        // Next month padding
        const remaining = 42 - days.length;
        for (let i = 1; i <= remaining; i++) {
            days.push(new Date(selectedYear, selectedMonth + 1, i));
        }

        return days;
    };

    // Get attendance status for a date
    const getAttendanceStatus = (date) => {
        if (!attendanceData?.monthly) return null;
        const dateStr = formatLocalDate(date);
        return attendanceData.monthly.find(record =>
            getISTDateFromUTC(record.date) === dateStr
        );
    };

    // Check if date is a weekly off based on employee's weekly_off configuration
    const isWeeklyOff = (date) => {
        if (!employeeDetails?.weekly_off) return false;

        const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
        const weeklyOff = employeeDetails.weekly_off.toLowerCase();

        // Check various weekly off configurations
        if (weeklyOff.includes('saturday') && weeklyOff.includes('sunday')) {
            return dayOfWeek === 0 || dayOfWeek === 6;
        } else if (weeklyOff.includes('sunday')) {
            return dayOfWeek === 0;
        } else if (weeklyOff.includes('saturday')) {
            return dayOfWeek === 6;
        } else if (weeklyOff.includes('monday')) {
            return dayOfWeek === 1;
        } else if (weeklyOff.includes('tuesday')) {
            return dayOfWeek === 2;
        } else if (weeklyOff.includes('wednesday')) {
            return dayOfWeek === 3;
        } else if (weeklyOff.includes('thursday')) {
            return dayOfWeek === 4;
        } else if (weeklyOff.includes('friday')) {
            return dayOfWeek === 5;
        }

        return false;
    };

    // Check if date is a holiday
    const getHolidayInfo = (date) => {
        const dateStr = formatLocalDate(date);
        return holidays.find(h => getISTDateFromUTC(h.date) === dateStr);
    };

    // Check if employee arrived early
    const isEarlyArrival = (attendance) => {
        if (!attendance?.check_in_time || !employeeDetails?.shift_start_time) return false;

        const checkInTime = parseApiTimestamp(attendance.check_in_time);
        if (!checkInTime) return false;

        const [shiftHour, shiftMin] = employeeDetails.shift_start_time.split(':').map(Number);
        const { hour: checkInHour, minute: checkInMin } = getISTComponents(checkInTime);

        const checkInMinutes = checkInHour * 60 + checkInMin;
        const shiftMinutes = shiftHour * 60 + shiftMin;

        return checkInMinutes < shiftMinutes;
    };

    // Check if employee arrived late
    const isLateArrival = (attendance) => {
        if (!attendance?.check_in_time || !employeeDetails?.shift_start_time) return false;

        const checkInTime = parseApiTimestamp(attendance.check_in_time);
        if (!checkInTime) return false;

        const [shiftHour, shiftMin] = employeeDetails.shift_start_time.split(':').map(Number);
        const gracePeriod = employeeDetails?.shift_grace_period || 15;

        const { hour: checkInHour, minute: checkInMin } = getISTComponents(checkInTime);

        const checkInMinutes = checkInHour * 60 + checkInMin;
        const shiftMinutes = shiftHour * 60 + shiftMin;
        const allowedLateMinutes = shiftMinutes + gracePeriod;

        return checkInMinutes > allowedLateMinutes;
    };

    // Navigate months
    const navigateMonth = (direction) => {
        if (direction === 'prev') {
            if (selectedMonth === 0) {
                setSelectedMonth(11);
                setSelectedYear(selectedYear - 1);
            } else {
                setSelectedMonth(selectedMonth - 1);
            }
        } else {
            if (selectedMonth === 11) {
                setSelectedMonth(0);
                setSelectedYear(selectedYear + 1);
            } else {
                setSelectedMonth(selectedMonth + 1);
            }
        }
    };

    // Handle day click
    const handleDayClick = (date, attendance, holiday) => {
        if (onDayClick) {
            onDayClick({ date, attendance, holiday });
        } else {
            setSelectedDayDetails({ date, attendance, holiday });
            setShowDayModal(true);
        }
    };

    // Handle attendance revoke (admin only)
    const handleRevokeAttendance = async () => {
        if (!selectedDayDetails?.attendance?.id) return;

        const reason = prompt('Please provide a reason for revoking this attendance:');
        if (!reason || reason.trim() === '') {
            alert('Reason is required to revoke attendance.');
            return;
        }

        try {
            const result = await attendanceService.revokeAttendance(selectedDayDetails.attendance.id, reason);
            alert(result.message || 'Attendance revoked successfully');
            setShowDayModal(false);
            setSelectedDayDetails(null);
            // Refresh data
            fetchAttendanceData();
            if (onRevokeAttendance) {
                onRevokeAttendance(selectedDayDetails.attendance);
            }
        } catch (error) {
            console.error('Error revoking attendance:', error);
            alert(error.response?.data?.error || 'Failed to revoke attendance');
        }
    };

    if (loading && !attendanceData) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Attendance Calendar</h2>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigateMonth('prev')}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </button>
                    <span className="text-sm font-medium text-gray-900 dark:text-white min-w-[120px] text-center">
                        {formatDate(new Date(selectedYear, selectedMonth), { format: 'long' }).replace(/\d+,?\s*/, '')}
                    </span>
                    <button
                        onClick={() => navigateMonth('next')}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </button>
                </div>
            </div>

            {/* Filter Controls */}
            {showFilters && (
                <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg flex-wrap">
                    <Filter className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter:</span>
                    {[
                        { key: 'all', label: 'All', color: 'bg-blue-500' },
                        { key: 'present', label: 'Present', color: 'bg-green-500' },
                        { key: 'absent', label: 'Absent', color: 'bg-red-500' },
                        { key: 'leaves', label: 'Leaves', color: 'bg-indigo-500' },
                        { key: 'wfh', label: 'WFH', color: 'bg-purple-500' },
                        { key: 'office', label: 'Office', color: 'bg-gray-500' },
                        { key: 'early', label: 'Early', color: 'bg-blue-500' },
                        { key: 'ontime', label: 'On-time', color: 'bg-green-500' },
                        { key: 'late', label: 'Late', color: 'bg-red-500' }
                    ].map(filter => (
                        <button
                            key={filter.key}
                            onClick={() => setCalendarFilter(filter.key)}
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                                calendarFilter === filter.key
                                    ? `${filter.color} text-white`
                                    : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                            }`}
                        >
                            {filter.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
                {/* Weekday Headers */}
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center py-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                        {day}
                    </div>
                ))}

                {/* Calendar Days */}
                {getCalendarDays().map((date, index) => {
                    const attendance = getAttendanceStatus(date);
                    const holiday = getHolidayInfo(date);
                    const isToday = isSameISTDay(date, new Date());
                    const isCurrentMonth = date.getMonth() === selectedMonth;
                    const isWeekend = isWeeklyOff(date);

                    const checkInMoment = attendance?.check_in_time ? parseApiTimestamp(attendance.check_in_time) : null;
                    const checkOutMoment = attendance?.check_out_time ? parseApiTimestamp(attendance.check_out_time) : null;
                    const overtimeHours = attendance?.overtime_hours ? parseFloat(attendance.overtime_hours) : 0;

                    // Determine status
                    let statusInfo = { label: '', color: '', icon: null, bgColor: '' };
                    if (holiday) {
                        statusInfo = {
                            label: 'Holiday',
                            color: 'text-purple-600 dark:text-purple-400',
                            bgColor: 'bg-purple-50 dark:bg-purple-900/20',
                            icon: <Award className="w-3 h-3" />
                        };
                    } else if (isWeekend) {
                        statusInfo = {
                            label: 'Weekly Off',
                            color: 'text-gray-600 dark:text-gray-400',
                            bgColor: 'bg-gray-50 dark:bg-gray-700/50',
                            icon: <Calendar className="w-3 h-3" />
                        };
                    } else if (attendance?.status === 'present' && !attendance?.check_out_time) {
                        statusInfo = {
                            label: 'Missing Checkout',
                            color: 'text-orange-600 dark:text-orange-400',
                            bgColor: 'bg-orange-50 dark:bg-orange-900/20',
                            icon: <AlertTriangle className="w-3 h-3" />
                        };
                    } else if (attendance?.status === 'present') {
                        statusInfo = {
                            label: 'Present',
                            color: 'text-green-600 dark:text-green-400',
                            bgColor: 'bg-green-50 dark:bg-green-900/20',
                            icon: <CheckCircle className="w-3 h-3" />
                        };
                    } else if (attendance?.status === 'absent') {
                        statusInfo = {
                            label: 'Absent',
                            color: 'text-red-600 dark:text-red-400',
                            bgColor: 'bg-red-50 dark:bg-red-900/20',
                            icon: <XCircle className="w-3 h-3" />
                        };
                    } else if (attendance?.status === 'leave') {
                        statusInfo = {
                            label: attendance?.leave_type || 'Leave',
                            color: 'text-blue-600 dark:text-blue-400',
                            bgColor: 'bg-blue-50 dark:bg-blue-900/20',
                            icon: <Coffee className="w-3 h-3" />
                        };
                    } else if (attendance?.status === 'half_day') {
                        statusInfo = {
                            label: 'Half Day',
                            color: 'text-yellow-600 dark:text-yellow-400',
                            bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
                            icon: <Clock className="w-3 h-3" />
                        };
                    } else if (isCurrentMonth && date < new Date() && !holiday && !isWeekend) {
                        statusInfo = {
                            label: 'Absent',
                            color: 'text-red-600 dark:text-red-400',
                            bgColor: 'bg-red-50 dark:bg-red-900/20',
                            icon: <XCircle className="w-3 h-3" />
                        };
                    }

                    // Calculate arrival status for filtering
                    const hasShift = !!employeeDetails?.shift_start_time;
                    const isEarly = attendance?.status === 'present' && isEarlyArrival(attendance);
                    const isLate = attendance?.status === 'present' && isLateArrival(attendance);
                    const isOnTime = attendance?.status === 'present' && hasShift && !isEarly && !isLate;
                    const isWfh = attendance?.status === 'present' && attendance?.is_wfh;
                    const isOffice = attendance?.status === 'present' && !attendance?.is_wfh;

                    // Filter logic
                    const shouldShow = calendarFilter === 'all' ||
                        (calendarFilter === 'present' && attendance?.status === 'present') ||
                        (calendarFilter === 'absent' && (attendance?.status === 'absent' || (!attendance && isCurrentMonth && date < new Date() && !holiday && !isWeekend))) ||
                        (calendarFilter === 'leaves' && attendance?.status === 'leave') ||
                        (calendarFilter === 'wfh' && isWfh) ||
                        (calendarFilter === 'office' && isOffice) ||
                        (calendarFilter === 'early' && isEarly) ||
                        (calendarFilter === 'ontime' && isOnTime) ||
                        (calendarFilter === 'late' && isLate);

                    if (!shouldShow && calendarFilter !== 'all') {
                        return (
                            <div
                                key={index}
                                className="relative p-2 min-h-[80px] border rounded-lg bg-gray-50/50 dark:bg-gray-800/50 opacity-30"
                            >
                                <span className="text-sm text-gray-400">{date.getDate()}</span>
                            </div>
                        );
                    }

                    return (
                        <div
                            key={index}
                            onClick={() => handleDayClick(date, attendance, holiday)}
                            onMouseEnter={() => setHoveredDate(formatLocalDate(date))}
                            onMouseLeave={() => setHoveredDate(null)}
                            className={`
                                relative p-2 min-h-[80px] border rounded-lg transition-all cursor-pointer group
                                ${!isCurrentMonth ? 'opacity-30' : ''}
                                ${isToday ? 'ring-2 ring-blue-500 dark:ring-blue-400 shadow-lg' : ''}
                                ${statusInfo.bgColor}
                                hover:shadow-md hover:scale-[1.02]
                            `}
                        >
                            {/* Header */}
                            <div className="flex items-start justify-between mb-1">
                                <div className="flex items-center gap-1 flex-wrap">
                                    <span className={`font-bold text-sm ${isToday ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                                        {date.getDate()}
                                    </span>
                                    {isToday && <span className="text-[9px] bg-blue-500 text-white px-1 rounded">Today</span>}

                                    {/* Location Badge */}
                                    {attendance?.status === 'present' && (
                                        attendance?.is_wfh ? (
                                            <span className="text-[9px] px-1 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                                                WFH
                                            </span>
                                        ) : (
                                            <span className="text-[9px] px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                                Office
                                            </span>
                                        )
                                    )}

                                    {/* Arrival Status Badge */}
                                    {attendance?.status === 'present' && (
                                        isLateArrival(attendance) ? (
                                            <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                                                Late
                                            </span>
                                        ) : isEarlyArrival(attendance) ? (
                                            <span className="text-[9px] px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                                Early
                                            </span>
                                        ) : (
                                            <span className="text-[9px] px-1 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                                On-time
                                            </span>
                                        )
                                    )}
                                </div>
                                {overtimeHours > 0 && <Zap className="w-3 h-3 text-amber-600 dark:text-amber-400" title="Overtime" />}
                            </div>

                            {/* Status Label */}
                            {statusInfo.label && (
                                <div className={`flex items-center gap-1 ${statusInfo.color}`}>
                                    {statusInfo.icon}
                                    <span className="text-[10px] font-semibold truncate">{statusInfo.label}</span>
                                </div>
                            )}

                            {/* Holiday Name */}
                            {holiday && (
                                <div className="text-[9px] text-purple-700 dark:text-purple-300 font-medium truncate">
                                    {holiday.name}
                                </div>
                            )}

                            {/* Time Info for Present Days */}
                            {attendance?.status === 'present' && checkInMoment && (
                                <div className="mt-1 text-[9px] text-gray-600 dark:text-gray-400 space-y-0.5">
                                    <div className="flex items-center gap-1">
                                        <LogIn className="w-2.5 h-2.5" />
                                        <span>{formatTime(checkInMoment)}</span>
                                    </div>
                                    {checkOutMoment && (
                                        <div className="flex items-center gap-1">
                                            <LogOut className="w-2.5 h-2.5" />
                                            <span>{formatTime(checkOutMoment)}</span>
                                        </div>
                                    )}
                                    {/* Display total work hours */}
                                    {attendance?.work_hours && parseFloat(attendance.work_hours) > 0 && (
                                        <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 font-medium">
                                            <Clock className="w-2.5 h-2.5" />
                                            <span>{parseFloat(attendance.work_hours).toFixed(1)}h</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs">
                {/* Status */}
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-green-100 dark:bg-green-900/30"></div>
                    <span className="text-gray-600 dark:text-gray-400">Present</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-red-100 dark:bg-red-900/30"></div>
                    <span className="text-gray-600 dark:text-gray-400">Absent</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-blue-100 dark:bg-blue-900/30"></div>
                    <span className="text-gray-600 dark:text-gray-400">Leave</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-purple-100 dark:bg-purple-900/30"></div>
                    <span className="text-gray-600 dark:text-gray-400">Holiday</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-gray-100 dark:bg-gray-700"></div>
                    <span className="text-gray-600 dark:text-gray-400">Weekly Off</span>
                </div>
                {/* Location */}
                <span className="text-gray-400 dark:text-gray-500">|</span>
                <div className="flex items-center gap-1">
                    <span className="px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[9px]">WFH</span>
                    <span className="text-gray-600 dark:text-gray-400">Work From Home</span>
                </div>
                <div className="flex items-center gap-1">
                    <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[9px]">Office</span>
                    <span className="text-gray-600 dark:text-gray-400">Office</span>
                </div>
                {/* Arrival */}
                <span className="text-gray-400 dark:text-gray-500">|</span>
                <div className="flex items-center gap-1">
                    <span className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[9px]">Early</span>
                    <span className="text-gray-600 dark:text-gray-400">Before shift</span>
                </div>
                <div className="flex items-center gap-1">
                    <span className="px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-[9px]">On-time</span>
                    <span className="text-gray-600 dark:text-gray-400">Within grace</span>
                </div>
                <div className="flex items-center gap-1">
                    <span className="px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-[9px]">Late</span>
                    <span className="text-gray-600 dark:text-gray-400">After grace</span>
                </div>
            </div>

            {/* Day Details Modal */}
            {showDayModal && selectedDayDetails && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {formatDate(selectedDayDetails.date, { format: 'full' })}
                                </h3>
                                <button
                                    onClick={() => setShowDayModal(false)}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                                >
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>

                            {selectedDayDetails.holiday && (
                                <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                                    <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                                        <Award className="w-5 h-5" />
                                        <span className="font-medium">{selectedDayDetails.holiday.name}</span>
                                    </div>
                                </div>
                            )}

                            {selectedDayDetails.attendance ? (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2">
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                            selectedDayDetails.attendance.status === 'present'
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                                : selectedDayDetails.attendance.status === 'absent'
                                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                        }`}>
                                            {selectedDayDetails.attendance.status?.charAt(0).toUpperCase() + selectedDayDetails.attendance.status?.slice(1)}
                                        </span>
                                    </div>

                                    {selectedDayDetails.attendance.check_in_time && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
                                                    <LogIn className="w-4 h-4" />
                                                    <span className="text-sm">Check In</span>
                                                </div>
                                                <p className="font-semibold text-gray-900 dark:text-white">
                                                    {formatTime(parseApiTimestamp(selectedDayDetails.attendance.check_in_time))}
                                                </p>
                                            </div>
                                            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
                                                    <LogOut className="w-4 h-4" />
                                                    <span className="text-sm">Check Out</span>
                                                </div>
                                                <p className="font-semibold text-gray-900 dark:text-white">
                                                    {selectedDayDetails.attendance.check_out_time
                                                        ? formatTime(parseApiTimestamp(selectedDayDetails.attendance.check_out_time))
                                                        : '-'
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Arrival Status - Early/On-time/Late with duration */}
                                    {selectedDayDetails.attendance.status === 'present' && selectedDayDetails.attendance.check_in_time && employeeDetails?.shift_start_time && (() => {
                                        const checkInTime = parseApiTimestamp(selectedDayDetails.attendance.check_in_time);
                                        if (!checkInTime) return null;

                                        const [shiftHour, shiftMin] = employeeDetails.shift_start_time.split(':').map(Number);
                                        const gracePeriod = employeeDetails?.shift_grace_period || 15;
                                        const { hour: checkInHour, minute: checkInMin } = getISTComponents(checkInTime);

                                        const checkInMinutes = checkInHour * 60 + checkInMin;
                                        const shiftMinutes = shiftHour * 60 + shiftMin;
                                        const allowedLateMinutes = shiftMinutes + gracePeriod;

                                        const diffMinutes = checkInMinutes - shiftMinutes;
                                        const absDiff = Math.abs(diffMinutes);
                                        const hours = Math.floor(absDiff / 60);
                                        const minutes = absDiff % 60;

                                        let durationText = '';
                                        if (hours > 0 && minutes > 0) {
                                            durationText = `${hours}h ${minutes}m`;
                                        } else if (hours > 0) {
                                            durationText = `${hours} hour${hours > 1 ? 's' : ''}`;
                                        } else {
                                            durationText = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
                                        }

                                        const shiftTimeFormatted = `${shiftHour > 12 ? shiftHour - 12 : shiftHour || 12}:${String(shiftMin).padStart(2, '0')} ${shiftHour >= 12 ? 'PM' : 'AM'}`;

                                        if (checkInMinutes < shiftMinutes) {
                                            // Early
                                            return (
                                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 rounded-lg">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                                            <span className="font-medium text-blue-800 dark:text-blue-200">Early Arrival</span>
                                                        </div>
                                                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-sm font-semibold">
                                                            {durationText} early
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                                                        Shift starts at {shiftTimeFormatted}
                                                    </p>
                                                </div>
                                            );
                                        } else if (checkInMinutes > allowedLateMinutes) {
                                            // Late
                                            const lateBy = checkInMinutes - shiftMinutes;
                                            const lateHours = Math.floor(lateBy / 60);
                                            const lateMinutes = lateBy % 60;
                                            let lateDurationText = '';
                                            if (lateHours > 0 && lateMinutes > 0) {
                                                lateDurationText = `${lateHours}h ${lateMinutes}m`;
                                            } else if (lateHours > 0) {
                                                lateDurationText = `${lateHours} hour${lateHours > 1 ? 's' : ''}`;
                                            } else {
                                                lateDurationText = `${lateMinutes} minute${lateMinutes !== 1 ? 's' : ''}`;
                                            }

                                            return (
                                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 rounded-lg">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                                            <span className="font-medium text-red-800 dark:text-red-200">Late Arrival</span>
                                                        </div>
                                                        <span className="px-2 py-1 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded text-sm font-semibold">
                                                            {lateDurationText} late
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                                                        Shift starts at {shiftTimeFormatted} (Grace: {gracePeriod} min)
                                                    </p>
                                                </div>
                                            );
                                        } else {
                                            // On-time
                                            return (
                                                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3 rounded-lg">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                                            <span className="font-medium text-green-800 dark:text-green-200">On-time Arrival</span>
                                                        </div>
                                                        <span className="px-2 py-1 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-sm font-semibold">
                                                            Within grace period
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                                                        Shift starts at {shiftTimeFormatted} (Grace: {gracePeriod} min)
                                                    </p>
                                                </div>
                                            );
                                        }
                                    })()}

                                    {selectedDayDetails.attendance.work_hours && (
                                        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-1">
                                                <Clock className="w-4 h-4" />
                                                <span className="text-sm">Work Hours</span>
                                            </div>
                                            <p className="font-semibold text-gray-900 dark:text-white">
                                                {parseFloat(selectedDayDetails.attendance.work_hours).toFixed(2)}h
                                            </p>
                                        </div>
                                    )}

                                    {selectedDayDetails.attendance.is_wfh && (
                                        <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400">
                                            <Home className="w-4 h-4" />
                                            <span>Work From Home</span>
                                        </div>
                                    )}

                                    {/* Admin Revoke Button */}
                                    {isAdmin && selectedDayDetails.attendance.id &&
                                     selectedDayDetails.attendance.status !== 'leave' &&
                                     !selectedDayDetails.holiday && (
                                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                                            <button
                                                onClick={handleRevokeAttendance}
                                                className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                                            >
                                                <XCircle className="w-4 h-4" />
                                                Revoke Attendance
                                            </button>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                                                This will delete the attendance record for this day
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    No attendance record for this day
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AttendanceCalendar;
